import "./LoginScreen.css";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { AppThemeContext } from "../../contexts/colors";
import { localeContext } from "../../contexts/localeManagement";
import { AuthContext } from "../../contexts/accountManagement";
import WebKit from "../../utils/web-kit";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropagateLoader from "react-spinners/PropagateLoader";
import { AnimatePresence, m, motion } from "motion/react";

import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faAt } from "@fortawesome/free-solid-svg-icons/faAt";
import { faRankingStar } from "@fortawesome/free-solid-svg-icons/faRankingStar";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

import { useLocation, useNavigate } from "react-router-dom";

function AnimatedFormGroup({ visibleCondition=true, formGroupStyles={}, formGroupIndex=0, children }) {
	return (
		<>
			<AnimatePresence initial={false}>
				{visibleCondition
					? <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0 }} id={`form-group-${formGroupIndex}`} className="form-group" style={formGroupStyles}>
						{children}
					</motion.div>
					: null}
			</AnimatePresence>
		</>
	)
}

const containsSequentialNumbers = (password) => {
	for (let i = 0; i < password.length - 2; i++) {
		let first = password.charCodeAt(i);
		let second = password.charCodeAt(i + 1);
		let third = password.charCodeAt(i + 2);

		if (first === second - 1 && second === third - 1) {
			return true;
		}
	}
	return false;
};

export default function AssignedRegistrationScreen() {
	const {
		dark,
		colors
	} = useContext(AppThemeContext);
	const { locale, appText } = useContext(localeContext);
	const { preLoggedIn, loggedIn, registerAssignedAccount, statusText } = useContext(AuthContext);
	const location = useLocation();

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [warnings, setWarnings] = useState([]);
	
	const [wrong, setWrong] = useState(false);
	const [wrongTxt, setWrongTxt] = useState("");

	const [ statusTextOpacity, setStatusTextOpacity ] = useState(0);
	const [ statusTextLocal, setStatusTextLocal ] = useState("");

	const navigate = useNavigate();

	const [ activeForm, setActiveForm ] = useState(0);
	const [ form0Active, setForm0Active ] = useState(true);
	const [ form1Active, setForm1Active ] = useState(false);

	useEffect(() => {
		if (activeForm === 0) {
			setForm1Active(false);
			setTimeout(() => {
				setForm0Active(true);
			}, 500);
		} else {
			setForm0Active(false);
			setTimeout(() => {
				setForm1Active(true);
			}, 500);
		}
	}, [activeForm]);

	const permTable = ["Teacher", "Supervisor", "Principal", "Governorate Agent"];
	const queryParams = new URLSearchParams(location.search);
	
	const token = queryParams.get("token") || "";
	const tokenPayload = WebKit.getJwtPayload(token);
	const email = queryParams.get("email") || "";
	const timestamp = parseInt(queryParams.get("timestamp")) || 0;
	const permissionLevel = permTable.indexOf(queryParams.get("level") || 0);

	const validatePassword = (password, confirmPassword) => {
		let tempWarnings = [];

		// Password length
		if (password.length < 8) {
			tempWarnings.push("Password must be at least 8 characters in length.");
		}

		// At least one symbol and one capital letter
		if (!/[A-Z]/.test(password) || !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
			tempWarnings.push("Password must contain at least one symbol and one capital letter.");
		}

		// Cannot contain three or more consecutive identical letters
		if (/(\w)\1\1/.test(password)) {
			tempWarnings.push("Password cannot contain three or more consecutive identical letters.");
		}

		// Cannot contain a number sequence of three or more
		if (containsSequentialNumbers(password)) {
			tempWarnings.push("Password cannot contain a number sequence of three or more characters.");
		}

		// Passwords must match
		if (password !== confirmPassword) {
			tempWarnings.push("Passwords must match.");
		}

		setWarnings(tempWarnings);
	}
	
	let loginStyles = useMemo(() => ({
		container: {
			display: "flex",
			flex: 1,
			flexDirection: "column",
			backgroundColor: dark ? colors().screen : colors().primary,
			justifyContent: "center",
			alignContent: "center",
			height: "100vh"
		},
		title: {
			color: dark ? colors().constantWhite : "#379634",
			textAlign: "center",
			fontSize: 40,
			fontFamily: locale === "ar" ? "ArabicMainBold" : "Ubuntu",
			fontWeight: locale === "ar"
				? "regular"
				: "bold",
			margin: 0
		},
		formGroup: {
			backgroundColor: colors().secondary,
			padding: 15,
			width: "35%",
			height: "60%",
			marginLeft: "auto",
			marginRight: "auto",
			borderRadius: 15,

			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between"
		},
		input: {
			backgroundColor: colors().success,
			width: "90%",
			marginLeft: "auto",
			marginRight: "auto",
			borderRadius: 15,
			padding: 10,
			paddingTop: locale === "ar" ? 0 : 10,
			paddingBottom: locale === "ar" ? 0 : 10,
			[locale === "ar" ? "paddingRight" : "paddingLeft"]: 12,
			fontFamily: locale === "ar" ? "ArabicMainNormal" : "Ubuntu",
			fontWeight: locale === "ar"
				? 400
				: "bold",
			fontSize: 22,
			color: "black",
			textAlign: locale === "ar" ? "right" : "left",
			border: "none",
			outline: "none"
		},
		inputShort: {
			height: 50 / 2,
			marginBottom: 20 / 2
		},
		inputParagraph: {
			height: 100 / 2
		},
		inputContainer: {
			display: "flex",
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: colors().success,
			borderRadius: 15,
			paddingLeft: 14,
			paddingRight: 14,
			width: "72.5%",
			marginLeft: "auto",
			marginRight: "auto"
		},
		sideBySide: {
			display: "flex",
			flexDirection: "row",
			width: "95%",
			marginLeft: "auto",
			marginRight: "auto"
		},
		btn: {
			padding: 10,
			backgroundColor: dark ? colors().constantWhite : colors().lightButton,
			width: "75%",
			marginLeft: "auto",
			marginRight: "auto",
			borderRadius: 15,
			height: 52,

			display: "flex",
			justifyContent: "center",
			alignContent: "center",
			alignItems: "center",
			flexDirection: "row",
			border: "none",
			outline: "none",

			transition: "300ms all ease"
		},
		btnText: {
			color: dark ? colors().mawjoodMain : colors().listItem,
			fontSize: 22,
			textAlign: "center",
			fontFamily: locale === "ar" ? "ArabicMainBold" : "Ubuntu",
			fontWeight: locale === "ar"
				? "regular"
				: "bold"
		},
		error: {
			display: "flex",
			flexDirection: "column",
			backgroundColor: colors().danger + "A2",
			paddingLeft: 20,
			paddingRight: 20,
			width: "72.5%",
			marginLeft: "auto",
			marginRight: "auto",
			borderRadius: 20,
			height: 80,
			justifyContent: "center",
			borderColor: colors().danger,
			borderWidth: 2,
			borderStyle: "solid",
			marginBottom: 10
		},
		errorTxt: {
			color: colors().constantWhite,
			fontSize: 17,
			fontWeight: "bold",
			textAlign: "center",
			fontFamily: "Ubuntu Mono"
		}
	}), [dark, locale]);

	/**
	 * @type {React.LegacyRef<HTMLInputElement>}
	 */
	const toggleShowPassword = () => {
		setShowPassword(!showPassword)
	}

	const handleAssignedRegistration = async () => {
		//* console.log("sending registration request")
		registerAssignedAccount(token, email, timestamp, permissionLevel, password)
			.then(res => {
				if (res.success) {
					setWrong(false);
					navigate("/");
				} else {
					setWrong(true);
					setWrongTxt(res.message);
				}
			})
	}

	useEffect(() => {
		//* console.log(statusText)
		if (statusTextLocal === "" && statusText !== "") {
			setStatusTextLocal(statusText);
			setStatusTextOpacity(1);
		}

		setStatusTextOpacity(0);
		setTimeout(() => {
			setStatusTextLocal(statusText);
			setStatusTextOpacity(1);
		}, 200);
	}, [statusText]);

	useEffect(() => {
		if (loggedIn) {
			navigate("/")
		}
	}, [loggedIn]);

	const handlePasswordChange = (e) => {
		const newPass = e.target.value;
		setPassword(newPass);
		validatePassword(newPass, confirmPassword);
	};

	const handleConfirmPasswordChange = (e) => {
		const newConfirmPass = e.target.value;
		setConfirmPassword(newConfirmPass);
		validatePassword(password, newConfirmPass);
	};

	return (
		<>
			<div style={loginStyles.container}>
				<AnimatedFormGroup formGroupIndex={0} formGroupStyles={loginStyles.formGroup} visibleCondition={form0Active}>
					<h1 style={loginStyles.title}>{/*appText[*/"View Your Details"/*]*/}</h1>
					<div style={loginStyles.form}>
						<div style={{ ...loginStyles.inputContainer, ...{
							marginBottom: 20,
							opacity: 0.8
						} }}>
							<input
								style={ {...loginStyles.input, ...loginStyles.inputShort, ...{
									marginBottom: 0,
									flex: 1,
									marginLeft: -15,
									opacity: 0.4,
									backgroundColor: "transparent"
								}} }
								value={email}
								readOnly
								disabled
								placeholder={"Email"}
							/>
							<FontAwesomeIcon
								icon={faEnvelope}
								size="xl"
								color={colors().primary}
								style={{ marginLeft: "auto", marginRight: "auto", opacity: 0.8 }}
							/>
						</div>
						<div style={{ ...loginStyles.inputContainer, ...{
							marginBottom: 20,
							opacity: 0.8
						} }}>
							<input
								style={ {...loginStyles.input, ...loginStyles.inputShort, ...{
									marginBottom: 0,
									flex: 1,
									marginLeft: -15,
									opacity: 0.4,
									backgroundColor: "transparent"
								}} }
								value={tokenPayload.name}
								readOnly
								disabled
								placeholder={"Username"}
							/>
							<FontAwesomeIcon
								icon={faAt}
								size="xl"
								color={colors().primary}
								style={{ marginLeft: "auto", marginRight: "auto", opacity: 0.8 }}
							/>
						</div>
						<div style={{ ...loginStyles.inputContainer, ...{
							opacity: 0.8
						} }}>
							<input
								style={ {...loginStyles.input, ...loginStyles.inputShort, ...{
									marginBottom: 0,
									flex: 1,
									marginLeft: -15,
									opacity: 0.4,
									backgroundColor: "transparent"
								}} }
								value={permTable[permissionLevel]}
								readOnly
								disabled
								placeholder={"Permission Level"}
							/>
							<FontAwesomeIcon
								icon={faRankingStar}
								size="xl"
								color={colors().primary}
								style={{ marginLeft: "auto", marginRight: "auto", opacity: 0.8 }}
							/>
						</div>
					</div>
					<button
						className="login-btn"
						style={{ ...loginStyles.btn, ...{
							alignSelf: "end",
							backgroundColor: dark ? colors().constantWhite : colors().lightButton,
							cursor: "pointer"
						} }}
						onClick={() => {setActiveForm(1)}}
					>
						<p style={{...loginStyles.btnText, ...{
							color: dark ? colors().mawjoodMain : colors().listItem,
							marginTop: "auto"
						}}}>{preLoggedIn
							? <PropagateLoader size={10} color={dark ? colors().mawjoodMain + "AA" : colors().listItem + "AA"} cssOverride={{ textAlign: "center" }} />
							: /*appText[*/"Next"/*].replace("تسجيل", "سجِّل")*/}
						</p>
					</button>
				</AnimatedFormGroup>
				<AnimatedFormGroup formGroupIndex={1} formGroupStyles={loginStyles.formGroup} visibleCondition={form1Active}>
					<h1 style={loginStyles.title}>{/*appText[*/"Register Your Account"/*]*/}</h1>
					<div style={loginStyles.form}>
					<div style={{ ...loginStyles.error, ...{
						display: wrong ? "flex" : "none",
						flexDirection: "column"
					} }}>
						<p style={loginStyles.errorTxt}>{appText[wrongTxt] || wrongTxt}</p>
					</div>
						<div style={{
							...loginStyles.inputContainer, ...{
								marginBottom: 20
							}
						}}>
							<input
								style={{
									...loginStyles.input, ...loginStyles.inputShort, ...{
										marginBottom: 0,
										flex: 1,
										marginLeft: -15
									}
								}}
								value={password}
								onChange={handlePasswordChange}
								placeholder={appText["Password"]}
								type={showPassword ? "text" : "password"}
								autoCapitalize="none"
								autoComplete="new-password"
								autoCorrect="none"
								disabled={preLoggedIn}
								readOnly={preLoggedIn}
							/>
							{showPassword
								? <IoMdEyeOff
									size={24}
									color={colors().primary}
									style={{ marginLeft: "auto", marginRight: "auto", cursor: "pointer" }}
									onMouseDown={e => {
										e.preventDefault();
										e.stopPropagation();
										toggleShowPassword();
									}}
								/>
								: <IoMdEye
									size={24}
									color={colors().primary}
									style={{ marginLeft: "auto", marginRight: "auto", cursor: "pointer" }}
									onMouseDown={e => {
										e.preventDefault();
										e.stopPropagation();
										toggleShowPassword();
									}}
								/>}
						</div>
						<div style={loginStyles.inputContainer}>
							<input
								style={{
									...loginStyles.input, ...loginStyles.inputShort, ...{
										marginBottom: 0,
										flex: 1,
										marginLeft: -15
									}
								}}
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								placeholder={"Confirm Password"}
								type={showPassword ? "text" : "password"}
								autoCapitalize="none"
								autoComplete="new-password"
								autoCorrect="none"
								onKeyDown={e => e.key === "Enter" && !(
									// eslint-disable-next-line no-useless-escape
									!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
									|| warnings.length > 0
									|| preLoggedIn) ? handleAssignedRegistration() : null}
								disabled={preLoggedIn}
								readOnly={preLoggedIn}
							/>
							{showPassword
								? <IoMdEyeOff
									size={24}
									color={colors().primary}
									style={{ marginLeft: "auto", marginRight: "auto", cursor: "pointer" }}
									onMouseDown={e => {
										e.preventDefault();
										e.stopPropagation();
										toggleShowPassword();
									}}
								/>
								: <IoMdEye
									size={24}
									color={colors().primary}
									style={{ marginLeft: "auto", marginRight: "auto", cursor: "pointer" }}
									onMouseDown={e => {
										e.preventDefault();
										e.stopPropagation();
										toggleShowPassword();
									}}
								/>}
						</div>
						{warnings.length > 0 && (
							<ul>
								{warnings.map((warning, index) => (
									<li key={index} style={{ color: colors().warning, fontWeight: "bold", fontFamily: "Courier New" }}>{warning}</li>
								))}
							</ul>
						)}
					</div>
					<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: -10 }}>
						<div style={loginStyles.sideBySide}>
							<button
								className="login-btn"
								style={{ ...loginStyles.btn, ...{
									alignSelf: "end",
									backgroundColor: !(preLoggedIn)
										? dark ? colors().constantWhite : colors().lightButton
										: dark ? colors().constantWhite + "AA" : colors().lightButton + "AA",
									cursor: "pointer",
									borderTopRightRadius: 0,
									borderBottomRightRadius: 0,
									marginRight: 2
								} }}
								onClick={() => {document.activeElement.blur(); setActiveForm(0)}}
								disabled={preLoggedIn}
							>
								<p style={{...loginStyles.btnText, ...{
									color: !(preLoggedIn)
										? dark ? colors().mawjoodMain : colors().listItem
										: dark ? colors().mawjoodMain + "AA" : colors().listItem + "AA",
									marginTop: "auto"
								}}}>{preLoggedIn
									? <PropagateLoader size={10} color={dark ? colors().mawjoodMain + "AA" : colors().listItem + "AA"} cssOverride={{ textAlign: "center" }} />
									: /*appText[*/"Back"/*].replace("تسجيل", "سجِّل")*/}
								</p>
							</button>
							<button
								className="login-btn"
								style={{ ...loginStyles.btn, ...{
									alignSelf: "end",
									backgroundColor: !(
										// eslint-disable-next-line no-useless-escape
										!email.match(/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
										|| warnings.length > 0
										|| preLoggedIn)
										? dark ? colors().constantWhite : colors().lightButton
										: dark ? colors().constantWhite + "AA" : colors().lightButton + "AA",
									cursor: "pointer",
									borderTopLeftRadius: 0,
									borderBottomLeftRadius: 0,
									marginLeft: 2
								} }}
								onClick={() => {document.activeElement.blur(); handleAssignedRegistration()}}
								disabled={
									// eslint-disable-next-line no-useless-escape
									!email.match(/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
									|| warnings.length > 0
									|| preLoggedIn}
							>
								<p style={{...loginStyles.btnText, ...{
									color: !(
										// eslint-disable-next-line no-useless-escape
										!email.match(/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
										|| warnings.length > 0
										|| preLoggedIn)
										? dark ? colors().mawjoodMain : colors().listItem
										: dark ? colors().mawjoodMain + "AA" : colors().listItem + "AA",
									marginTop: "auto"
								}}}>{preLoggedIn
									? <PropagateLoader size={10} color={dark ? colors().mawjoodMain + "AA" : colors().listItem + "AA"} cssOverride={{ textAlign: "center" }} />
									: /*appText[*/"Register"/*].replace("تسجيل", "سجِّل")*/}
								</p>
							</button>
						</div>
						<p
							style={{
								fontSize: 20,
								color: colors().mawjoodOther,
								textAlign: "center",
								fontFamily: "Courier New",
								fontWeight: "bold",
								margin: 0,
								marginTop: 5,
								opacity: statusTextOpacity,
								transition: "200ms all ease"
							}}
						>
							{appText[statusTextLocal] || statusTextLocal}
						</p>
					</div>
				</AnimatedFormGroup>
			</div>
		</>
	);
}